import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Finding } from '../../types/findings'
import type { BloomLevel } from '../../types'

interface FindingsInboxProps {
  findings: Finding[]
  onIntegrate: (finding: Finding) => void
  onDismiss: (finding: Finding) => void
}

const TYPE_ICONS: Record<Finding['type'], string> = {
  node_added: '⬡',
  bloom_updated: '↑',
  edge_added: '↔',
}

const TYPE_COLORS: Record<Finding['type'], string> = {
  node_added: 'text-green-400',
  bloom_updated: 'text-yellow-400',
  edge_added: 'text-blue-400',
}

export function FindingsInbox({ findings, onIntegrate, onDismiss }: FindingsInboxProps) {
  const { t } = useTranslation(['preparation', 'session'])
  const [collapsed, setCollapsed] = useState(false)

  const pending = findings.filter(f => f.status === 'pending')
  if (pending.length === 0) return null

  // Bloom level labels use the shared session namespace to avoid duplication
  function getBloomLabel(level: BloomLevel | undefined): string {
    if (!level) return '—'
    return t(`session:bloomTracker.levels.${level}`)
  }

  // Teaching form labels use the shared session namespace to avoid duplication
  function getFormLabel(form: string): string {
    return t(`session:forms.${form}.title`, { defaultValue: form })
  }

  function getDescription(finding: Finding): string {
    switch (finding.type) {
      case 'node_added':
        return `${finding.data.label} — Bloom: ${getBloomLabel(finding.data.bloomLevel as BloomLevel | undefined)}`
      case 'bloom_updated':
        return t('preparation:findings.bloomChange', {
          label: finding.data.label,
          from: getBloomLabel(finding.data.previousBloomLevel as BloomLevel | undefined),
          to: getBloomLabel(finding.data.bloomLevel as BloomLevel | undefined),
        })
      case 'edge_added': {
        const symbol = finding.data.relationType === 'causal' ? '→'
          : finding.data.relationType === 'interdependent' ? '↔' : '—'
        return `${finding.data.edgeSource} ${symbol} ${finding.data.edgeTarget}`
      }
      default:
        return ''
    }
  }

  return (
    <div className="bg-green-950/30 border-b border-green-900/30 px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="bg-green-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
            {t('findings.badge', { count: pending.length })}
          </span>
          <span className="text-green-400 font-semibold text-sm">
            {t('findings.title')}
          </span>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-green-400 text-xs hover:text-green-300"
        >
          {collapsed ? t('findings.show') : t('findings.hide')}
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-1.5">
          {pending.map(finding => (
            <div
              key={finding.id}
              className="bg-green-950/40 border border-green-900/30 rounded-md px-3 py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className={TYPE_COLORS[finding.type]}>
                    {TYPE_ICONS[finding.type]} {t(`findings.${finding.type === 'node_added' ? 'nodeAdded' : finding.type === 'bloom_updated' ? 'bloomUpdated' : 'edgeAdded'}`)}
                  </span>
                  <span className="text-gray-500">
                    {t('preparation:findings.fromSession', {
                      form: getFormLabel(finding.teachingForm),
                    })}
                  </span>
                </div>
                <div className="text-sm text-gray-200 mt-0.5 truncate">
                  {getDescription(finding)}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => onIntegrate(finding)}
                  className="bg-green-500/20 text-green-400 px-2.5 py-1 rounded text-xs hover:bg-green-500/30"
                >
                  {t('findings.integrate')}
                </button>
                <button
                  onClick={() => onDismiss(finding)}
                  className="bg-gray-700/50 text-gray-400 px-2.5 py-1 rounded text-xs hover:bg-gray-700/70"
                >
                  {t('findings.dismiss')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
